import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { FormEvent, KeyboardEvent, MouseEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SurpriseButton, { SurpriseTextsType } from "../../../../../../components/common/SurpriseButton/SurpriseButton";
import { APPS } from "../../../../../../constants/AppsConstants";
import { TG_PROMPT_INPUT_LIGHT_HEIGHT } from "../../../../../../constants/TextToImageConstants";
import { useAppStore } from "../../../../../../hooks/useStores";
import TextToImageError from "../../../../../Playground/TextToImage/Generator/Search/Error/TextToImageError";
import styles from "./TgPromptInput.module.scss";

type Props = {
	prefix?: string;
	placeholder?: string;
	prompt?: string;
	onChange?: (newPrompt: string) => void;
	onEnter?: () => void;
	surpriseTextsList?: SurpriseTextsType;
	disabled?: boolean;
	className?: string;
	fontSize?: string;
	maxRows?: number;
};

const TgPromptInput = ({
	prefix = "",
	prompt = "",
	placeholder = "",
	onChange,
	onEnter,
	surpriseTextsList,
	disabled,
	className,
	fontSize,
	maxRows = 8,
}: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "app.promptInput" });
	const inputRef = useRef<HTMLSpanElement>(null);
	const textPromptRef = useRef<HTMLDivElement>(null);
	const isUserTypingRef = useRef(false);
	const { textToImageStore, playgroundStore } = useAppStore();
	const isNewTg = playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION;
	const hideError = textToImageStore.errors?.generate?.status === 422;

	useEffect(() => {
		const options = { passive: true, capture: true };
		const handleSelectStart = (e: Event) => {
			const parentRef = textPromptRef.current as HTMLDivElement;
			parentRef.contains(e.target as HTMLElement) && handleFocus();
		};

		if (textPromptRef.current) document.addEventListener("selectstart", handleSelectStart, options);

		return () => {
			document.removeEventListener("selectstart", handleSelectStart, options);
		};
	}, []);

	const updateInputHeight = () => {
		if (!inputRef.current) return;
		const el = inputRef.current;
		el.style.height = "auto";
		const scrollHeight = el.scrollHeight;
		const maxHeight = TG_PROMPT_INPUT_LIGHT_HEIGHT * maxRows;
		el.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
	};

	const positionCursor = () => {
		const range = document.createRange();
		const selection = window.getSelection();
		range.selectNodeContents(inputRef.current!);
		range.collapse(false);
		selection?.removeAllRanges();
		selection?.addRange(range);
	};

	useEffect(() => {
		if (!inputRef.current) return;

		if (disabled) {
			inputRef.current.innerText = prompt || "";
			return;
		}

		if (!isUserTypingRef.current) {
			if (!prompt) {
				if (!prefix) {
					inputRef.current.innerText = "";
				} else {
					inputRef.current.innerText = " ";
				}
			} else {
				inputRef.current.innerText = prompt;
				positionCursor();
			}
		}

		isUserTypingRef.current = false;
		updateInputHeight();
	}, [prompt, disabled, prefix]);

	const handleFocus = () => {
		if (disabled) return;
		inputRef.current?.focus();
	};

	const handleInput = (e: FormEvent<HTMLSpanElement>) => {
		if (disabled) return;
		isUserTypingRef.current = true;
		const rawText = e.currentTarget.innerText;
		const trimmedText = rawText.trim();
		onChange?.(trimmedText);

		if (e.currentTarget.innerText === "") {
			e.currentTarget.innerText = " ";
		}

		updateInputHeight();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
		if (disabled) return;
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			onEnter?.();
		}
	};

	const handleSurpriseClick = (_e: MouseEvent<HTMLButtonElement>, surprisedText: string) => {
		isUserTypingRef.current = false;
		onChange?.(surprisedText);
		if (inputRef.current) {
			inputRef.current.innerText = surprisedText || " ";
			inputRef.current.focus();
			updateInputHeight();
		}
	};

	const disableSelecto = (isDisabled: boolean) => {
		playgroundStore.disableSelecto = isDisabled;
	};

	return (
		<Box
			className={clsx(
				styles.inputWrapper,
				{ [styles.disabled]: disabled, [styles.sideInput]: maxRows === 8 },
				className,
			)}
			onMouseEnter={() => disableSelecto(true)}
			onMouseLeave={() => disableSelecto(false)}
			onClick={handleFocus}
			ref={textPromptRef}
		>
			{isNewTg && !hideError && (
				<TextToImageError
					anchorRef={textPromptRef}
					containerRef={textToImageStore.imageGenerationContainerRef}
				/>
			)}
			<Box className={styles.prompt} sx={{ maxHeight: `${TG_PROMPT_INPUT_LIGHT_HEIGHT * maxRows}px` }}>
				<span className={styles.prefixLine}>
					{!disabled && prefix && (
						<span className={styles.prefixTag} style={{ fontSize }}>
							{t("prefix.tag")}
						</span>
					)}
					{prefix && (
						<span className={styles.prefixText} style={{ fontSize }}>
							{prefix}
						</span>
					)}
					<span
						ref={inputRef}
						className={styles.inputPrompt}
						style={{ fontSize, maxHeight: `${TG_PROMPT_INPUT_LIGHT_HEIGHT * maxRows}px !important` }}
						contentEditable={!disabled}
						tabIndex={disabled ? -1 : 0}
						suppressContentEditableWarning
						onInput={handleInput}
						onKeyDown={handleKeyDown}
						data-placeholder={!prefix && !prompt ? placeholder : ""}
						dir="ltr"
					/>
				</span>
			</Box>

			{surpriseTextsList && (
				<Box className={styles.footer}>
					<Typography className={styles.footerText}>{t("footer")}</Typography>
					<SurpriseButton
						className={styles.surpriseBtn}
						buttonType="textSmall"
						textsListType={surpriseTextsList}
						onSurprise={handleSurpriseClick}
					/>
				</Box>
			)}
		</Box>
	);
};

export default observer(TgPromptInput);
